import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AppState, DataLayerMessage, DeviceType, FashPlayProductData, FashSizeProductData } from '../types';
import DataLayerService from '../services/DataLayerService';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import debugLogger from '../utils/DebugLogger';

const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
const isUrlDemoMode = params ? params.get('demoMode') === 'true' : false;
const urlDemoPersonId = params ? params.get('demoPersonId') : null;

// Initial state - always open for iframe usage
const initialState: AppState = {
  isOpen: true,
  currentPage: 'home',
  deviceType: 'desktop',
  parentOrigin: '*',
  basketItems: [],
  currentProduct: null,
  userPreferences: {},
  brandId: isUrlDemoMode ? 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' : null,
  brandName: isUrlDemoMode ? 'Demo Brand' : null,
  language: 'tr', // Default language
  demoMode: isUrlDemoMode,
  demoPersonId: urlDemoPersonId
};

const persistAiEndpointMode = (payload: any) => {
  const value = payload?.metadata?.useLegacyAiEndpoints;
  if (typeof window !== 'undefined' && typeof value === 'boolean') {
    window.sessionStorage.setItem('FC_USE_LEGACY_AI_ENDPOINTS', value ? 'true' : 'false');
    window.localStorage.removeItem('FC_USE_LEGACY_AI_ENDPOINTS');
  }
};

// Action types
type AppAction =
  | { type: 'SET_OPEN'; payload: boolean }
  | { type: 'SET_CURRENT_PAGE'; payload: string }
  | { type: 'SET_DEVICE_TYPE'; payload: DeviceType }
  | { type: 'SET_PARENT_ORIGIN'; payload: string }
  | { type: 'SET_BASKET_ITEMS'; payload: any[] }
  | { type: 'SET_CURRENT_PRODUCT'; payload: any }
  | { type: 'SET_USER_PREFERENCES'; payload: any }
  | { type: 'SET_BRAND_INFO'; payload: { brandId: string; brandName: string } }
  | { type: 'HANDLE_MESSAGE'; payload: DataLayerMessage }
  | { type: 'SET_LANGUAGE'; payload: 'tr' | 'en' }
  | { type: 'RESET_STATE' };

// Reducer
const appReducer = (state: AppState, action: AppAction): AppState => {
  switch (action.type) {
    case 'SET_OPEN':
      return { ...state, isOpen: action.payload };

    case 'SET_CURRENT_PAGE':
      return { ...state, currentPage: action.payload };

    case 'SET_DEVICE_TYPE':
      return { ...state, deviceType: action.payload };

    case 'SET_PARENT_ORIGIN':
      return { ...state, parentOrigin: action.payload };

    case 'SET_BASKET_ITEMS':
      return { ...state, basketItems: action.payload };

    case 'SET_CURRENT_PRODUCT':
      return { ...state, currentProduct: action.payload };

    case 'SET_USER_PREFERENCES':
      return { ...state, userPreferences: action.payload };

    case 'SET_BRAND_INFO':
      return {
        ...state,
        brandId: action.payload.brandId,
        brandName: action.payload.brandName
      };

    case 'HANDLE_MESSAGE':
      return getStateFromMessage(state, action.payload);

    case 'SET_LANGUAGE':
      return { ...state, language: action.payload };

    case 'RESET_STATE':
      return {
        ...initialState,
        deviceType: state.deviceType, // Keep device type
        isOpen: state.isOpen, // Keep open state
        parentOrigin: state.parentOrigin // Keep parent origin
      };

    default:
      return state;
  }
};

// Get new state from message (without navigation)
const getStateFromMessage = (state: AppState, message: DataLayerMessage): AppState => {
  switch (message.type) {
    case 'basket-combin':
    case 'basket-combination-page':
      return {
        ...state,
        isOpen: true,
        currentPage: 'basket-combination',
        basketItems: message.payload?.basketItems || state.basketItems,
        brandId: message.payload?.brandInfo?.brandId || state.brandId,
        brandName: message.payload?.brandInfo?.brandName || state.brandName,
        userPreferences: {
          ...state.userPreferences,
          ...message.payload?.userInfo?.preferences
        }
      };

    case 'init':
      const initBrandId = message.payload?.brandInfo?.brandId || state.brandId;
      const isDemoBrand = initBrandId === 'a1b2c3d4-e5f6-7890-abcd-ef1234567890';
      return {
        ...state,
        parentOrigin: message.payload?.origin || state.parentOrigin,
        userPreferences: message.payload?.preferences || state.userPreferences,
        brandId: initBrandId,
        brandName: message.payload?.brandInfo?.brandName || state.brandName,
        demoMode: state.demoMode || isDemoBrand || message.payload?.demoMode || false
      };

    case 'close':
      return {
        ...state,
        isOpen: false,
        currentPage: 'home'
      };

    case 'OPEN_FASHSNAP':
      persistAiEndpointMode(message.payload);
      const snapBrandId = message.payload?.brandInfo?.brandId || state.brandId;
      return {
        ...state,
        isOpen: true,
        currentPage: 'fash-snap',
        fashSnapProduct: message.payload || null,
        brandId: snapBrandId,
        brandName: message.payload?.brandInfo?.brandName || state.brandName,
        demoMode: state.demoMode || message.payload?.demoMode || snapBrandId === 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' || false,
        demoPersonId: message.payload?.demoPersonId || state.demoPersonId
      };

    case 'OPEN_FASHPLAY':
      const fashPlayPayload = message.payload || {};
      persistAiEndpointMode(fashPlayPayload);
      const fashPlayProduct: FashPlayProductData = {
        productId: fashPlayPayload.productId,
        productMeta: fashPlayPayload.productMeta,
      };
      const playBrandId = message.payload?.brandInfo?.brandId || state.brandId;

      debugLogger.info('Opening FashPlay');

      return {
        ...state,
        isOpen: true,
        currentPage: 'fash-play',
        fashPlayProduct,
        brandId: playBrandId,
        brandName: message.payload?.brandInfo?.brandName || state.brandName,
        demoMode: state.demoMode || message.payload?.demoMode || playBrandId === 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' || false,
        demoPersonId: message.payload?.demoPersonId || state.demoPersonId
      };

    case 'OPEN_FASHFAST':
      const fashFastPayload = message.payload || {};
      persistAiEndpointMode(fashFastPayload);
      const fashFastProduct: FashPlayProductData = {
        productId: fashFastPayload.productId,
        productMeta: fashFastPayload.productMeta,
      };
      const fastBrandId = message.payload?.brandInfo?.brandId || state.brandId;

      debugLogger.info('Opening FashFast');

      return {
        ...state,
        isOpen: true,
        currentPage: 'fashfast',
        fashFastProduct, // We can reuse FashPlayProductData shape
        brandId: fastBrandId,
        brandName: message.payload?.brandInfo?.brandName || state.brandName,
        demoMode: state.demoMode || message.payload?.demoMode || fastBrandId === 'a1b2c3d4-e5f6-7890-abcd-ef1234567890' || false,
        demoPersonId: message.payload?.demoPersonId || state.demoPersonId
      };

    case 'OPEN_FASHSIZE': {
      const fashSizePayload = message.payload || {};
      const fashSizeProduct: FashSizeProductData = {
        productId: fashSizePayload.productId,
        productMeta: fashSizePayload.productMeta,
        category: fashSizePayload.category,
      };
      const sizeBrandId = fashSizePayload.brandInfo?.brandId || state.brandId;
      debugLogger.info('Opening Fashsize');
      return {
        ...state,
        isOpen: true,
        currentPage: 'fash-size',
        fashSizeProduct,
        brandId: sizeBrandId,
        brandName: fashSizePayload.brandInfo?.brandName || state.brandName,
        demoMode: state.demoMode || fashSizePayload.demoMode || false,
      };
    }

    case 'DEMO_SELECT_PERSON':
      return {
        ...state,
        demoMode: true,
        demoPersonId: message.payload?.personId || null
      };

    default:
      return state;
  }
};

// Handle routing based on message
const handleMessageRouting = (message: DataLayerMessage, navigate: any) => {
  debugLogger.info(`Handling routing for message type: ${message.type}`);

  switch (message.type) {
    case 'basket-combin':
    case 'basket-combination-page':
      debugLogger.info('Received basket combination request - navigating');
      try {
        navigate('/basket-combination');
        debugLogger.success('Navigation triggered for: /basket-combination');
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    case 'close':
      debugLogger.info('Received close message - navigating to home');
      try {
        navigate('/');
        debugLogger.success('Navigation triggered for: /');
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    case 'OPEN_FASHSNAP':
      debugLogger.info('Received OPEN_FASHSNAP - navigating to FashSnap');
      try {
        navigate('/fash-snap');
        debugLogger.success('Navigation triggered for: /fash-snap');
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    case 'OPEN_FASHPLAY':
      debugLogger.info('Received OPEN_FASHPLAY - navigating to FashPlay');
      try {
        const isFastMode = message.payload?.isFastMode;
        navigate('/fash-play', { state: isFastMode ? { isFastMode: true } : undefined });
        debugLogger.success(`Navigation triggered for: /fash-play ${isFastMode ? '(Fast Mode)' : ''}`);
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    case 'OPEN_FASHFAST':
      debugLogger.info('Received OPEN_FASHFAST - navigating to FashFast');
      try {
        navigate('/fash-fast');
        debugLogger.success('Navigation triggered for: /fash-fast');
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    case 'OPEN_FASHSIZE':
      debugLogger.info('Received OPEN_FASHSIZE - navigating to Fashsize');
      try {
        navigate('/fash-size');
        debugLogger.success('Navigation triggered for: /fash-size');
      } catch (e) {
        debugLogger.error('Navigation failed', e);
      }
      break;

    default:
      debugLogger.info(`No routing needed for message type: ${message.type}`);
  }
};

// Context
interface AppContextType {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
  openWidget: (page?: string) => void;
  closeWidget: () => void;
  navigateToPage: (page: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// Provider
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const deviceType = useDeviceDetection();
  const navigate = useNavigate();
  const location = useLocation();

  // Update device type
  useEffect(() => {
    dispatch({ type: 'SET_DEVICE_TYPE', payload: deviceType });
  }, [deviceType]);

  // Update current page based on URL
  useEffect(() => {
    const pathToPage: { [key: string]: string } = {
      '/': 'home',
      '/home': 'home',
      '/basket-combination': 'basket-combination',
      '/fash-snap': 'fash-snap',
      '/fash-play': 'fash-play',
      '/fash-fast': 'fashfast',
      '/fash-size': 'fash-size'
    };

    const currentPage = pathToPage[location.pathname] || 'home';
    debugLogger.info(`Route changed to: ${location.pathname}, Page: ${currentPage}`);
    dispatch({ type: 'SET_CURRENT_PAGE', payload: currentPage });
  }, [location.pathname]);

  // Setup DataLayer listeners
  useEffect(() => {
    debugLogger.info('Setting up DataLayer listeners');

    const unsubscribers = [
      'basket-combin',
      'basket-combination-page',
      'OPEN_FASHSNAP',
      'OPEN_FASHPLAY',
      'OPEN_FASHFAST',
      'OPEN_FASHSIZE',
      'init',
      'close',
      'DEMO_SELECT_PERSON'
    ].map(messageType =>
      DataLayerService.subscribe(messageType as any, (message) => {
        debugLogger.info(`Listener triggered for ${messageType}`, message.payload);
        // Handle routing FIRST
        handleMessageRouting(message, navigate);
        // Then update state
        dispatch({ type: 'HANDLE_MESSAGE', payload: message });
      })
    );

    // Notify parent that widget is ready
    debugLogger.info('Notifying parent that widget is ready');
    DataLayerService.notifyReady();

    return () => {
      debugLogger.info('Cleaning up DataLayer listeners');
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [navigate]);

  // Helper functions
  const openWidget = (page: string = 'home') => {
    dispatch({ type: 'SET_OPEN', payload: true });
    dispatch({ type: 'SET_CURRENT_PAGE', payload: page });
  };

  const closeWidget = () => {
    // Reset state when closing widget
    dispatch({ type: 'RESET_STATE' });
    dispatch({ type: 'SET_OPEN', payload: false });
    DataLayerService.notifyClose();
  };

  const navigateToPage = (page: string) => {
    const pageToPath: { [key: string]: string } = {
      'home': '/',
      'basket-combination': '/basket-combination',
      'fash-snap': '/fash-snap',
      'fash-play': '/fash-play',
      'fash-size': '/fash-size'
    };

    const path = pageToPath[page] || '/';
    navigate(path);
  };

  const contextValue: AppContextType = {
    state,
    dispatch,
    openWidget,
    closeWidget,
    navigateToPage
  };

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

// Hook
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
